

export const RESIZED_IMAGE_MAX_WIDTH = 720;
export const RESIZED_IMAGE_MAX_HEIGHT = 1440;
export const RESIZED_IMAGE_QUALITY = 0.8;

export interface IDeviceFile {
  base64: string;
  dataUrl: string;
  fileName: string;
  contentType: string;
  rawFile: File;
}

export const processUserSelectedFile = (file: File): Promise<IDeviceFile | null> => {
  return new Promise<IDeviceFile | null>((resolve) => {
    const reader = new FileReader();

    reader.onload = async () => {
      const dataUrl = reader.result as string;

      resolve({
        fileName: file.name,
        contentType: file.type,
        base64: dataUrl.split(',')[1],
        dataUrl: dataUrl,
        rawFile: file
      });
    };

    reader.onerror = () => {
      resolve(null);
    };

    reader.readAsDataURL(file);


  })
}

export const convertBase64ToFile = (base64: string, contentType: string, fileName: string): File => {
  const cleanBase64 = base64.includes(',') ? base64.split(',')[1] : base64;

  // Decode base64 to binary data
  const byteCharacters = atob(cleanBase64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // Create a Blob from the binary data
  const blob = new Blob([byteArray], { type: contentType });

  // Create a File object from the Blob
  return new File([blob], fileName, { type: contentType });
}

export const getDeviceFile = (acceptedMimeTypes: string[]): Promise<IDeviceFile | null> => {
  return new Promise<IDeviceFile | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptedMimeTypes.join(',');

    input.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const file: File | null = target.files ? target.files[0] : null;
      
      if (file) {
        const deviceFile = await processUserSelectedFile(file);
        resolve(deviceFile);
      } else {
        resolve(null);
      }
    };

    input.click();
  });
}

export const getDeviceImage = (): Promise<IDeviceFile | null> => {
  return new Promise<IDeviceFile | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png';

    input.onchange = async (event) => {
      const target = event.target as HTMLInputElement;
      const file: File | null = target.files ? target.files[0] : null;
      
      if (file) {
        const deviceFile = await processUserSelectedFile(file);
        if (!deviceFile) {
          resolve(null);
          return;
        }

        const resizedResult = await resizeAndLowerQuality(deviceFile.dataUrl, RESIZED_IMAGE_MAX_WIDTH, RESIZED_IMAGE_MAX_HEIGHT, RESIZED_IMAGE_QUALITY);
        if (resizedResult) {
          const rawFile = convertBase64ToFile(resizedResult.base64, resizedResult.contentType, file.name);
          resolve({
            fileName: file.name,
            contentType: resizedResult.contentType,
            base64: resizedResult.base64,
            dataUrl: resizedResult.dataUrl,
            rawFile: rawFile
          });
        } else {
          resolve(deviceFile);
        }
      } else {
        resolve(null);
      }
    };

    input.click();
  });
}

export const resizeAndLowerImageFileQuality = async (file: File, maxWidth?: number, maxHeight?: number, quality?: number): Promise<File | null> => {
  const deviceFile = await processUserSelectedFile(file);
  if (!deviceFile) {
    return null;
  }

  const resizedImage = await resizeAndLowerQuality(deviceFile.dataUrl, maxWidth, maxHeight, quality);
  if (!resizedImage) {
    return null;
  }

  return convertBase64ToFile(resizedImage.base64, resizedImage.contentType, file.name);
}

export const resizeAndLowerQuality = (
  dataUrl: string,
  maxWidth?: number,
  maxHeight?: number,
  quality?: number,
  /**
   * Scale the image down until it fits inside BOTH bounds, rather than only capping its longer
   * edge (the default, which leaves e.g. a 4:3 landscape photo taller than maxHeight). Opt-in so
   * existing callers keep the sizes they already produce.
   */
  containWithinBounds?: boolean
): Promise<{ dataUrl: string, base64: string, contentType: string } | null> => {
  return new Promise<{ dataUrl: string, base64: string, contentType: string } | null>((resolve) => {

    const contentType = dataUrl.split(';')[0].split(':').pop()!;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      const maxWidthToUse = maxWidth ?? RESIZED_IMAGE_MAX_WIDTH;
      const maxHeightToUse = maxHeight ?? RESIZED_IMAGE_MAX_HEIGHT;
      const qualityToUse = quality ?? RESIZED_IMAGE_QUALITY;

      // Calculate the new dimensions while maintaining the aspect ratio
      if (containWithinBounds) {
        const scale = Math.min(1, maxWidthToUse / width, maxHeightToUse / height);
        width *= scale;
        height *= scale;
      } else if (width > height) {
        if (width > maxWidthToUse) {
          height *= maxWidthToUse / width;
          width = maxWidthToUse;
        }
      } else {
        if (height > maxHeightToUse) {
          width *= maxHeightToUse / height;
          height = maxHeightToUse;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, width, height);

      // Convert the canvas to a base64 string with the specified quality
      const newBase64 = canvas.toDataURL(contentType, qualityToUse);
      resolve({
        dataUrl: newBase64,
        base64: newBase64.split(',')[1],
        contentType
      });
    };
    img.onerror = () => {
      resolve(null);
    };

    // Set the image source to the base64 string
    img.src = dataUrl;
  });
};

/**
 * Files exactly as the user chose them, WITHOUT reading a byte.
 *
 * `getDeviceFile` above routes every pick through `readAsDataURL`, which base64s the whole file
 * into memory — fine for a five megabyte document, fatal for a five hundred megabyte class video.
 * A raw `File` can be handed straight to `xhr.send()`, which streams it off disk instead.
 *
 * Resolves with an empty array when the picker is dismissed: a cancelled dialog fires no `change`
 * event at all, so without the `cancel` handler the promise would never settle and the caller would
 * sit waiting for a file that is never coming.
 */
export const pickRawFiles = (accept: string, multiple: boolean): Promise<File[]> => {
  return new Promise<File[]>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.multiple = multiple;

    input.onchange = () => resolve(Array.from(input.files ?? []));
    input.oncancel = () => resolve([]);

    input.click();
  });
}
